// Import auth service for authenticated requests
import { authService } from './authService';
import { getServerURL } from './config';
import { saveAs } from 'file-saver';

// Orders Service for comprehensive order management
const SERVER_URL = getServerURL();

export const ordersService = {
  // Test API connectivity
  async testConnection() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      return false;
    }

    try {
      console.log('Testing connection to:', `${SERVER_URL}/orders/health`);
      const response = await fetch(`${SERVER_URL}/orders/health`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });
      console.log('Connection test response status:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  },

  // ==================== ORDER MANAGEMENT ====================

  // Get all orders with filtering and pagination
  async getOrders(filters = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams();

      // Add all filter parameters with proper handling
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // For arrays, join with comma (backend will parse)
            if (value.length > 0) {
              queryParams.append(key, value.join(','));
            }
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const response = await fetch(`${SERVER_URL}/orders?${queryParams.toString()}`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // Return the full response to maintain compatibility with both old and new API structures
        return data.success ? data : { orders: [], pagination: {} };
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch orders: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  // Get an order with its server-confirmed country namespace.
  async getOrderDetail(orderId, resourceCountry = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const normalizedCountry = resourceCountry
        ? String(resourceCountry).trim().toUpperCase()
        : null;
      if (
        normalizedCountry
        && !authService.getAllowedCountries().includes(normalizedCountry)
      ) {
        throw new Error('Order Detail country is not authorized');
      }
      const headers = {
        ...authService.getAuthHeaders(),
        ...(normalizedCountry ? { 'X-Country': normalizedCountry } : {}),
      };
      const response = await fetch(
        `${SERVER_URL}/orders/${encodeURIComponent(orderId)}`,
        {
          headers,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (!data.success) return null;

        const responseCountry = response.headers?.get('X-Country') || null;
        if (
          responseCountry
          && normalizedCountry
          && responseCountry.toUpperCase() !== normalizedCountry
        ) {
          throw new Error('Order Detail country mismatch');
        }

        return {
          order: data.data,
          country: responseCountry || normalizedCountry,
        };
      } else if (response.status === 404) {
        return null;
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch order: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  // Backward-compatible single-order accessor.
  async getOrderById(orderId) {
    const detail = await this.getOrderDetail(orderId);
    return detail?.order || null;
  },

  async removeLineItemImage(orderId, lineItemId, photoName, imageUrl) {
    const response = await fetch(
      `${SERVER_URL}/orders/${encodeURIComponent(orderId)}`
        + `/line-items/${encodeURIComponent(lineItemId)}/images`,
      {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ photoName, imageUrl }),
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to remove photo');
    }
    return data;
  },

  async getAdminSettings() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/api/admin/settings`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        return await response.json();
      }

      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to fetch admin settings: ${response.status}`);
    } catch (error) {
      console.error('Error fetching admin settings:', error);
      throw error;
    }
  },

  // Create a manual order using the backend's structured order form contract.
  async createManualOrder(orderData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/create-manual`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || errorData.message || `Failed to create order: ${response.status}`);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  // Backward-compatible service alias. Manual orders must never use flat POST /orders.
  async createOrder(orderData) {
    return this.createManualOrder(orderData);
  },

  // Fetch the story catalog available for storefront-style order creation.
  async getStoryCatalog({ refresh = false } = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(
        `${SERVER_URL}/orders/catalog/stories${refresh ? '?refresh=1' : ''}`,
        {
          method: 'GET',
          headers: authService.getAuthHeaders(),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        return data.data || [];
      }

      throw new Error(data.error || `Failed to load story catalog: ${response.status}`);
    } catch (error) {
      console.error('Error loading story catalog:', error);
      throw error;
    }
  },

  // Shipping options (Regular, Express, Free) with live Shopify rates.
  async getShippingOptions() {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }
    const response = await fetch(`${SERVER_URL}/orders/shipping-options`, {
      method: 'GET',
      headers: authService.getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data.success) return data.data || [];
    throw new Error(data.error || `Failed to load shipping options: ${response.status}`);
  },

  // Calculate a storefront quote for an already-built quote request.
  async calculateStorefrontQuote(request) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/calculate-shopify-cart`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(request),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        return data.quote;
      }

      throw new Error(data.error || 'Unable to calculate order pricing');
    } catch (error) {
      console.error('Error calculating storefront quote:', error);
      throw error;
    }
  },

  // Submit a storefront-style order. Manual orders must never use flat POST /orders.
  async createStorefrontOrder(payload) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        return data.data;
      }

      const error = new Error(data.error || data.message || 'Order submission failed');
      error.details = data.details;
      error.status = response.status;
      throw error;
    } catch (error) {
      console.error('Error submitting storefront order:', error);
      throw error;
    }
  },

  async _postOrderEdit(path, body, failureMessage) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}${path}`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        // The edit endpoints report warnings beside `data`, not inside it.
        if (data.warnings && data.data && !data.data.warnings) {
          return { ...data.data, warnings: data.warnings };
        }
        return data.data;
      }

      const error = new Error(data.error || data.message || failureMessage);
      error.details = data.details;
      error.status = response.status;
      throw error;
    } catch (error) {
      console.error(`${failureMessage}:`, error);
      throw error;
    }
  },

  // Add a story (and its add-ons) to an existing order.
  async addStoryToOrder(orderId, { items, price_override, edit_request_id } = {}) {
    return this._postOrderEdit(
      `/orders/${encodeURIComponent(orderId)}/edit/add-story`,
      { items, price_override, edit_request_id },
      'Failed to add story to order'
    );
  },

  // Dry-run a staged order edit; returns the computed preview.
  async previewOrderEdit(orderId, body) {
    return this._postOrderEdit(
      `/orders/${encodeURIComponent(orderId)}/edit/preview`,
      body,
      'Failed to preview order changes'
    );
  },

  // Apply a staged order edit in one save.
  async saveOrderEdit(orderId, body) {
    return this._postOrderEdit(
      `/orders/${encodeURIComponent(orderId)}/edit`,
      body,
      'Failed to save order changes'
    );
  },

  // Replace the story on an existing line item.
  async replaceOrderStory(orderId, lineItemId, { items, price_override, edit_request_id } = {}) {
    return this._postOrderEdit(
      `/orders/${encodeURIComponent(orderId)}/line-items/${encodeURIComponent(lineItemId)}/edit/replace-story`,
      { items, price_override, edit_request_id },
      'Failed to replace story'
    );
  },

  // Record an additional manual payment; returns { payment, balance }.
  async recordAdditionalPayment(orderId, { amount, method, reference, notes, payment_request_id } = {}) {
    return this._postOrderEdit(
      `/orders/${encodeURIComponent(orderId)}/payments/additional`,
      { amount, method, reference, notes, payment_request_id },
      'Failed to record payment'
    );
  },

  // Upload a kid photo for an order. Multipart requests must not set Content-Type
  // so the browser can add the boundary.
  async uploadOrderPhoto(file) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const headers = { ...authService.getAuthHeaders() };
      delete headers['Content-Type'];
      delete headers['content-type'];

      const formData = new FormData();
      formData.append('kid_image', file);

      const response = await fetch(`${SERVER_URL}/dashboard/order-photos`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        return data.url;
      }

      throw new Error(data.error || 'Photo upload failed. Please try again.');
    } catch (error) {
      console.error('Error uploading order photo:', error);
      throw error;
    }
  },

  // Confirm an order after support verification.
  async confirmOrder(orderId, notes = '') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const confirmationData = {
        confirmation_method: 'support',
        ...(notes.trim() ? { notes: notes.trim() } : {})
      };
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/confirm`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(confirmationData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? (data.data || data) : null;
      }

      const errorData = await response.json().catch(() => ({}));
      console.error('API Error:', response.status, errorData);
      throw new Error(errorData.error || errorData.message || `Failed to confirm order: ${response.status}`);
    } catch (error) {
      console.error('Error confirming order:', error);
      throw error;
    }
  },

  async recordItemReturn(orderId, itemId, reason, notes = '') {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    const response = await fetch(`${SERVER_URL}/orders/${orderId}/item-returns`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({
        item_ids: [itemId],
        reason: reason.trim(),
        ...(notes.trim() ? { notes: notes.trim() } : {})
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `Failed to record item return: ${response.status}`);
    }
    return data.data || data;
  },

  async createItemRedelivery(orderId, itemId, shippingFee, provider) {
    if (!SERVER_URL) throw new Error('API server URL is not configured');
    const response = await fetch(`${SERVER_URL}/orders/${orderId}/redeliveries`, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: JSON.stringify({
        item_ids: [itemId],
        shipping_fee: shippingFee,
        provider
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.error || data.message || `Failed to create redelivery: ${response.status}`);
    }
    return data.data || data;
  },

  // Update order status
  async updateOrderStatus(orderId, statusData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(statusData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to update order status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  // Add comment to order
  async addComment(orderId, commentData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/comments`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(commentData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to add comment: ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  // Add flag to order
  async addFlag(orderId, flagData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/flags`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(flagData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to add flag: ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding flag:', error);
      throw error;
    }
  },

  // Resolve flag
  async resolveFlag(orderId, flagId, resolutionNotes = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const body = resolutionNotes ? { resolution_notes: resolutionNotes } : {};

      const response = await fetch(`${SERVER_URL}/orders/${orderId}/flags/${flagId}/resolve`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to resolve flag: ${response.status}`);
      }
    } catch (error) {
      console.error('Error resolving flag:', error);
      throw error;
    }
  },

  // ==================== STATISTICS & ANALYTICS ====================

  // Get order statistics
  async getOrderStats(period = '7d', groupBy = 'status') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams({ period, group_by: groupBy });
      const response = await fetch(`${SERVER_URL}/orders/stats?${queryParams.toString()}`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : {};
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch order statistics: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching order statistics:', error);
      throw error;
    }
  },

  // Get quick statistics for dashboard
  async getQuickStats(period = 'all', source = null, excludeSource = null, dateFilters = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams({ period });
      if (source) {
        queryParams.set('source', source);
      } else if (excludeSource) {
        queryParams.set('exclude_source', excludeSource);
      }
      if (dateFilters.date_range) {
        queryParams.set('date_range', dateFilters.date_range);
      }
      if (dateFilters.start_date) {
        queryParams.set('start_date', dateFilters.start_date);
      }
      if (dateFilters.end_date) {
        queryParams.set('end_date', dateFilters.end_date);
      }
      const response = await fetch(`${SERVER_URL}/orders/quick-stats?${queryParams.toString()}`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch quick statistics: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching quick statistics:', error);
      throw error;
    }
  },

  // Update shipping address
  async updateShippingAddress(orderId, addressData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/shipping-address`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressData),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update address: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating shipping address:', error);
      throw error;
    }
  },

  // Get orders ready for print
  async getReadyForPrint() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/ready-for-print`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : { orders: [], total: 0 };
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch ready-for-print orders: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching ready-for-print orders:', error);
      throw error;
    }
  },

  // ==================== REVIEWER MANAGEMENT ====================

  // Get available reviewers
  async getAvailableReviewers() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/reviewers`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : [];
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch available reviewers: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching available reviewers:', error);
      throw error;
    }
  },

  // Assign reviewer to order
  async assignReviewer(orderId, assignmentData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/assign-reviewer`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(assignmentData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to assign reviewer: ${response.status}`);
      }
    } catch (error) {
      console.error('Error assigning reviewer:', error);
      throw error;
    }
  },

  // Auto-assign reviewer to order
  async autoAssignReviewer(orderId, priority = 'normal') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/auto-assign-reviewer`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ priority }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to auto-assign reviewer: ${response.status}`);
      }
    } catch (error) {
      console.error('Error auto-assigning reviewer:', error);
      throw error;
    }
  },

  // Get reviewer workload
  async getReviewerWorkload(reviewerId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/reviewers/${reviewerId}/workload`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : {};
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch reviewer workload: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching reviewer workload:', error);
      throw error;
    }
  },

  // Get reviewer performance
  async getReviewerPerformance(reviewerId, period = '30d') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const queryParams = new URLSearchParams({ period });
      const response = await fetch(`${SERVER_URL}/reviewers/${reviewerId}/performance?${queryParams.toString()}`, {
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data.data : {};
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch reviewer performance: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching reviewer performance:', error);
      throw error;
    }
  },

  // ==================== UTILITY METHODS ====================

  // Format order data for display (updated for unified schema)
  formatOrderForDisplay(order) {
    if (!order) return null;

    // Handle both old and new schema formats
    const totalAmount = order.total_price || order.payment?.amount || order.payment?.total || order.total_amount || 0;
    const currency = order.currency || order.payment?.currency || 'EGP';

    return {
      ...order,
      // Use line_items if available, fall back to items
      items: order.line_items || order.items || [],

      formattedTotal: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(parseFloat(totalAmount) || 0),

      formattedCreatedAt: new Date(order.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),

      formattedUpdatedAt: new Date(order.updated_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),

      statusDisplay: order.status?.replace(/_/g, ' ').toUpperCase(),
      priorityDisplay: order.priority?.toUpperCase(),

      // Financial status display
      financialStatusDisplay: order.financial_status?.replace(/_/g, ' ').toUpperCase(),

      // Shopify info
      shopifyOrderNumber: order.shopify_order_number || order.metadata?.shopify_order_number,
      isShopifyOrder: order.source === 'shopify',

      // Customer display name
      customerDisplayName: order.customer?.name ||
        `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim() ||
        'Unknown Customer'
    };
  },

  // Import provider-owned Shopify order authority through the reconciler.
  async importShopifyOrder({
    requestId,
    country,
    store,
    shopifyOrderId
  }) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/import/shopify`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          request_id: requestId,
          country,
          store,
          shopify_order_id: shopifyOrderId
        }),
      });

      const data = await response.json();
      const terminal = data?.data;
      if (
        !response.ok
        || data?.success !== true
        || terminal?.request_id !== requestId
        || terminal?.country !== country
        || terminal?.store !== store
        || terminal?.state !== 'completed'
        || !['imported', 'reconciled'].includes(terminal?.result?.status)
        || typeof terminal?.result?.order_id !== 'string'
      ) {
        throw new Error('safe_shopify_import_failure');
      }
      return terminal;
    } catch (error) {
      console.error('Shopify import request failed');
      throw new Error('Shopify import could not be completed');
    }
  },

  // ==================== MANUAL ACTIONS ====================

  // Send manual WhatsApp notification
  async sendManualWhatsApp(orderId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/send-whatsapp`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          manual: true,
          timestamp: new Date().toISOString(),
          userId: options.userId || 'unknown',
          ...options
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.message || `Failed to send WhatsApp: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending manual WhatsApp:', error);
      throw error;
    }
  },

  // Regenerate trial manually
  async regenerateTrial(trialId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/trials/${trialId}/regenerate`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          reason: options.reason || 'Manual regeneration requested',
          userId: options.userId || 'unknown',
          ...options
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.message || `Failed to regenerate trial: ${response.status}`);
      }
    } catch (error) {
      console.error('Error regenerating trial:', error);
      throw error;
    }
  },

  // Merge orders manually
  async mergeOrders(targetOrderId, sourceOrderId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${targetOrderId}/merge`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ source_order_id: sourceOrderId }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to merge orders: ${response.status}`);
      }
    } catch (error) {
      console.error('Error merging orders:', error);
      throw error;
    }
  },

  // Validate order data before submission
  validateOrderData(orderData) {
    const errors = [];

    if (!orderData.customer?.email) {
      errors.push('Customer email is required');
    }

    if (!orderData.customer?.name) {
      errors.push('Customer name is required');
    }

    if (!orderData.items || orderData.items.length === 0) {
      errors.push('At least one item is required');
    }

    if (!orderData.payment?.total || orderData.payment.total <= 0) {
      errors.push('Valid payment total is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // ==================== PAYMENT UPDATE METHODS ====================

  /**
   * Update payment method with optional Shopify sync
   */
  async updatePaymentMethod(orderId, method, syncToShopify = true) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/payments/method`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ method, syncToShopify }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to update payment method: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    }
  },

  /**
   * Update both payment method and status with optional Shopify sync
   */
  async updatePaymentInfo(orderId, paymentData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/payments`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(paymentData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to update payment information: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating payment information:', error);
      throw error;
    }
  },

  async getOrderRefunds(orderId) {
    const response = await fetch(`${SERVER_URL}/orders/${encodeURIComponent(orderId)}/refunds`, {
      headers: authService.getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `Failed to load refunds: ${response.status}`);
    }
    return data.data || [];
  },

  async issueRefund(
    orderId,
    refundData,
    idempotencyKey = `refund-${Date.now()}-${Math.random().toString(36).slice(2)}`
  ) {
    const response = await fetch(`${SERVER_URL}/orders/${encodeURIComponent(orderId)}/refunds`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Idempotency-Key': idempotencyKey
      },
      body: JSON.stringify(refundData),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `Failed to record refund: ${response.status}`);
    }
    return data.data;
  },

  // ==================== BOSTA SHIPPING ====================

  /**
   * Download Bosta AWB (Air Waybill) for an order
   */
  async downloadBostaAWB(orderId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    const { requestedAwbType = 'A4', lang = 'ar' } = options;

    try {
      const response = await fetch(`${SERVER_URL}/printings/${orderId}/bosta/download-awb`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ requestedAwbType, lang }),
      });

      if (response.ok) {
        const filename = `${orderId}.pdf`;
        const arrayBuffer = await response.arrayBuffer();
        
        // Convert to base64 data URL to bypass blob UUID naming
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = window.btoa(binary);
        const dataUrl = `data:application/pdf;base64,${base64}`;
        
        // Create anchor with data URL - this forces the download attribute to work
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        
        // Use native MouseEvent to bypass React Router interception
        link.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, view: window }));

        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to download AWB: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading Bosta AWB:', error);
      throw error;
    }
  },

  /**
   * Download Shipping Company Label (PNG image)
   * Used for orders dispatched via the Shipping Company App
   */
  async downloadShippingCompanyLabel(orderId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/printings/${orderId}/shipping-company/download-label`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const filename = `${orderId}.png`;
        const arrayBuffer = await response.arrayBuffer();
        
        // Convert to base64 data URL to bypass blob UUID naming
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = window.btoa(binary);
        const dataUrl = `data:image/png;base64,${base64}`;
        
        // Create anchor with data URL - this forces the download attribute to work
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        
        // Use native MouseEvent to bypass React Router interception
        link.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, view: window }));

        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to download label: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading Shipping Company label:', error);
      throw error;
    }
  },

  /**
   * Recreate/Resync Order in Bosta
   * Used when AWB download fails due to missing delivery
   */
  async recreateBostaOrder(orderId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/recreate-bosta-order`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to recreate Bosta order: ${response.status}`);
      }
    } catch (error) {
      console.error('Error recreating Bosta order:', error);
      throw error;
    }
  },

  /**
   * Sync order statuses from Bosta delivery system
   * Checks all non-shipped/non-cancelled orders and updates to "shipped" if delivered
   */
  async syncBostaStatus() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/sync-bosta-status`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to sync Bosta status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error syncing Bosta status:', error);
      throw error;
    }
  },

  // ==================== LINE ITEM MANAGEMENT ====================

  /**
   * Add images to a line item
   * @param {string} orderId - The order ID
   * @param {string} lineItemId - The line item ID
   * @param {string[]} imageUrls - Array of image URLs to add
   */
  async addLineItemImages(orderId, lineItemId, imageUrls) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/line-items/${lineItemId}/images`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ imageUrls }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to add line item images: ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding line item images:', error);
      throw error;
    }
  },

  /**
   * Update line item story - allows Super Admin or Operations to change story
   * @param {string} orderId - The order ID
   * @param {string} lineItemId - The line item ID
   * @param {Object} updateData - Update data
   * @param {string} updateData.story_id - New story ID
   * @param {string} updateData.kid_name - Kid name (optional)
   * @param {string} updateData.reason - Reason for change (required, min 10 chars)
   */
  async updateLineItemStory(orderId, lineItemId, updateData) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/line-items/${lineItemId}/story`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.success ? data : null;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to update line item story: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating line item story:', error);
      throw error;
    }
  },

  /**
   * Generate a single line item - creates a generation document
   * Used for line items that have requires_generation=true but no generation_id,
   * or items whose status hasn't started yet (new/pending)
   * @param {string} orderId - The order ID
   * @param {string} lineItemId - The line item ID
   */
  async generateLineItem(orderId, lineItemId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/line-items/${lineItemId}/generate`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to generate line item: ${response.status}`);
      }
    } catch (error) {
      console.error('Error generating line item:', error);
      throw error;
    }
  },

  /**
   * Add addon services to an existing order (Express, Gift, Gift Note + price adjustment)
   * @param {string} orderId - The order ID
   * @param {Object} addons - Addon configuration
   * @param {boolean} [addons.express] - Enable express for all items
   * @param {string[]} [addons.giftLineItemIds] - Line item IDs to mark as gift
   * @param {Array<{lineItemId: string, note: string}>} [addons.giftNotes] - Gift notes per item
   * @param {number} [addons.newPrice] - New total price (manual adjustment)
   */
  async addOrderAddons(orderId, addons) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/orders/${orderId}/addons`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(addons),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to add addons: ${response.status}`);
      }
    } catch (error) {
      console.error('Error adding order addons:', error);
      throw error;
    }
  }
};
